import { useQuery } from '@tanstack/react-query';
import { getCurrentSubscription, getSubscriptionTiers } from '@/api/subscriptions';
//import type { CurrentSubscription } from '@/types/subscription';

/**
 * Hook to fetch the current user's subscription details.
 */
export function useCurrentSubscription() {
  return useQuery({
    queryKey: ['subscription', 'current'],
    queryFn: getCurrentSubscription,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch available subscription tiers.
 */
export function useSubscriptionTiers() {
  return useQuery({
    queryKey: ['subscription', 'tiers'],
    queryFn: getSubscriptionTiers,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook that provides subscription-aware permission checks.
 */
export function useSubscriptionLimits() {
  const { data: subscription, isLoading: isSubscriptionLoading } = useCurrentSubscription();

  const canCreateSets = subscription?.limits.can_create_sets ?? false;

  const canCreateMessageThread = (() => {
    if (!subscription) return true; // Optimistic while loading
    const { tier, limits, usage_today, usage_lifetime } = subscription;

    if (tier === 'free') {
      const max = limits.max_message_threads_lifetime;
      if (max === null) return true;
      return usage_lifetime.message_threads_initiated < max;
    } else {
      const max = limits.max_message_threads_per_day;
      if (max === null) return true;
      return usage_today.message_threads_initiated < max;
    }
  })();

  const canGenerateTasks = (() => {
    if (!subscription) return true; // Optimistic while loading
    const { tier, limits, usage_today, usage_lifetime } = subscription;

    if (tier === 'free') {
      const max = limits.max_tasks_lifetime;
      if (max === null) return true;
      return usage_lifetime.tasks_generated < max;
    } else {
      const max = limits.max_tasks_per_day;
      if (max === null) return true;
      return usage_today.tasks_generated < max;
    }
  })();

  const isFreeTier = subscription?.tier === 'free';
  const hasProactiveMessages = subscription?.limits.proactive_messages_enabled ?? false;
  const canAccessAnalytics = subscription ? subscription.tier !== 'free' : false;

  return {
    subscription,
    isSubscriptionLoading,
    canCreateSets,
    canCreateMessageThread,
    canGenerateTasks,
    isFreeTier,
    hasProactiveMessages,
    canAccessAnalytics,
  };
}
